import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createWarehouse, updateWarehouse } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { SiteWarehouseDTO } from "src/app/models/Sites/siteWarehouseDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { CategoriesAndPropsUtils } from "src/app/shared/utils/categoriesAndPropsUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

//Warehouse Detail Form
const WAREHOUSE_FORM_PARAMS = {
  enabled: "enabled",
  defaultWarehouse: "defaultWarehouse",
  warehouseCode: "warehouseCode",
  serialNumber: "serialNumber",
  description: "description",
  warehouseType: "warehouseType",
  siteProduct: "siteProduct",
  totalCapacity: "totalCapacity",
  properties: "properties",
};

@Component({
  selector: "app-warehouse-form-dialog",
  templateUrl: "./warehouse-form-dialog.component.html",
  styleUrls: ["./warehouse-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class WarehouseFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getWarehouseForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;

  siteCode: string;
  warehouseType: string;
  currentWarehouse: SiteWarehouseDTO;

  warehouseTypeList: SiteWarehouseDTO[];
  siteProducts: SiteProductDTO[];

  propertyList: PropertyDTO[];
  
  isCreate: boolean;
  warehouseDataForm: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<WarehouseFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createWarehouseForm();
    this.validateFormValues();
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode,
    this.warehouseType = this.data.warehouseType;
    this.currentWarehouse = this.data.currentWarehouse;
    this.warehouseTypeList = this.data.warehouseTypeList;
    this.siteProducts = this.data.siteProducts;
    this.propertyList = this.data.propertyList;
  }

  createWarehouseForm() {
    this.warehouseDataForm = this.fb.group({
      [WAREHOUSE_FORM_PARAMS.enabled]: [true],
      [WAREHOUSE_FORM_PARAMS.defaultWarehouse]: [
        { value: null, disabled: this.warehouseType === 'WET' },
      ],
      [WAREHOUSE_FORM_PARAMS.warehouseCode]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [WAREHOUSE_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [WAREHOUSE_FORM_PARAMS.description]: [
        null,
        [Validators.required, FormUtils.noWhitespaceValidator, Validators.maxLength(45)],
      ],
      [WAREHOUSE_FORM_PARAMS.warehouseType]: [
        { value: null, disabled: true }, 
        Validators.required,
      ],
      [WAREHOUSE_FORM_PARAMS.siteProduct]: [
        { value: null, disabled: this.warehouseType === 'DRY' },
        Validators.required,
      ],
      [WAREHOUSE_FORM_PARAMS.totalCapacity]: [
        { value: null, disabled: this.warehouseType === 'DRY' },
        [Validators.min(0), Validators.max(9999999999), Validators.required],
      ],
      [WAREHOUSE_FORM_PARAMS.properties]: this.fb.array([]),
    });
    this.subscribeFormChanges(this.warehouseDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getWarehousePristineValue();
    });
  }

  validateFormValues() {
    if (this.isCreate) {
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.warehouseType
      ].setValue(this.warehouseType);
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.enabled
      ].setValue(true);
    } else {
      this.setFormValues(this.currentWarehouse);
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.siteProduct
      ].disable();
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.warehouseType
      ].disable();
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.totalCapacity
      ].disable();
      if (this.currentWarehouse.warehouseType.code === "WET") {
        this.warehouseDataForm.controls[
          WAREHOUSE_FORM_PARAMS.defaultWarehouse
        ].disable();
      } else {
        this.warehouseDataForm.controls[
          WAREHOUSE_FORM_PARAMS.defaultWarehouse
        ].enable();
      }
    }
  }

  setFormValues(warehouse: SiteWarehouseDTO, duplication?: boolean) {
    this.warehouseDataForm.controls.enabled.setValue(
      warehouse?.enabled ?? true
    );
    this.warehouseDataForm.controls.warehouseCode.setValue(
      duplication ? null : warehouse?.warehouseCode ?? null
    );
    this.warehouseDataForm.controls.serialNumber.setValue(
      duplication ? null : warehouse?.serialNumber ?? null
    );
    this.warehouseDataForm.controls.description.setValue(
      warehouse?.description ?? null
    );
    this.warehouseDataForm.controls.warehouseType.setValue(
      warehouse?.warehouseType?.code ?? null
    );
    this.warehouseDataForm.controls.siteProduct.setValue(
      warehouse?.siteProduct?.code ?? null
    );
    this.warehouseDataForm.controls.totalCapacity.setValue(
      warehouse?.totalCapacity ?? null
    );
    this.warehouseDataForm.controls.defaultWarehouse.setValue(
      warehouse?.defaultWarehouse ?? false
    );
  }

  disableFormField(form: FormGroup, formField: string) {
    form.get(formField).clearValidators();
    form.get(formField).updateValueAndValidity();
    form.get(formField).setValue(null);
    form.get(formField).disable();
  }

  enableFormField(form: FormGroup, formField: string) {
    form.get(formField).setValue(null);
    form.get(formField).enable();
  }

  requireFormField(form: FormGroup, formField: string) {
    form.get(formField).setValidators([Validators.required]);
  }

  duplicate() {
    this.setFormValues(this.currentWarehouse, true);

    this.isCreate = true;
    this.currentWarehouse = null;

    if (
      this.warehouseDataForm.controls[WAREHOUSE_FORM_PARAMS.warehouseType]
        .value === "WET"
    ) {
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.siteProduct
      ].enable();
      this.warehouseDataForm.controls[
        WAREHOUSE_FORM_PARAMS.totalCapacity
      ].enable();
    }
  }

  onSubmit() {
    if (!this.warehouseDataForm.valid) {
      this.warehouseDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.warehouseDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let warehouse: SiteWarehouseDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentWarehouse?.code,
      warehouseCode: this.warehouseDataForm.controls.warehouseCode.value ?? null,
      serialNumber: this.warehouseDataForm.controls.serialNumber.value ?? null,
      enabled: this.warehouseDataForm.controls.enabled.value ?? true,
      description: this.warehouseDataForm.controls.description.value ?? null,
      warehouseTypeCode: this.warehouseDataForm.controls.warehouseType.value ?? null,
      productCode: this.warehouseDataForm.controls.siteProduct.value ?? null,
      totalCapacity: this.warehouseDataForm.controls.totalCapacity.value ?? null,
      defaultWarehouse: this.warehouseDataForm.controls.defaultWarehouse.value ?? false,
      properties: CategoriesAndPropsUtils.setFormProperties(
        this.warehouseDataForm.controls.properties.value
      ),
    };

    if (this.isCreate) {
      this.store.dispatch(createWarehouse({ warehouse }));
    } else {
      this.store.dispatch(updateWarehouse({ warehouse }));
    }

    this.warehouseDataForm.markAsUntouched();
    this.warehouseDataForm.markAsPristine();
    this.getWarehousePristineValue();
  }

  checkStockStatus() {
    return !this.currentWarehouse.stockStatus.length;
  }

  goToStock() {
    this.dialogRef.close();
    const {
      stockStatus: [{ code }],
    } = this.currentWarehouse;
    this.router.navigate(["fuelStocks/fuelStock", code]);
  }

  getWarehousePristineValue() {
    this.getWarehouseForm.emit(this.warehouseDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.warehouseDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}